import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconCamera, IconPhoto, IconSend, IconCheck, IconX, IconAlertTriangle, IconPlus, IconDeviceMobilePlus, IconShare, IconDotsVertical, IconLock } from '@tabler/icons-react';
import { Switch } from '@/components/ui/switch';
import BoothPdfFace from '../../components/common/BoothPdfFace';
import { notifications } from '../../lib/notifications';
import photoCollectorService from '../../services/photoCollectorService';
import { BOOTH_ACCEPT, BOOTH_PHOTO_ACCEPT, checkBoothFile, formatBoothSize } from '../../utils/boothFiles';
import { toBnDigits } from '../../utils/bnFormat';
import { useBoothInstall } from './useBoothInstall';

// Remember the customer's number across visits so they don't retype it each time.
const PHONE_KEY = 'sk_booth_phone';
// …and whether they asked for what they send to be deleted once it has been printed.
const PRIVATE_KEY = 'sk_booth_private';

// ── Input hygiene (backend re-sanitizes + validates too) ─────────────────────
// Any-country friendly: phone = digits, 6–20. (No name is collected anymore.)
const phoneDigitsOf = (p) => p.replace(/\D/g, '');
const phoneOk = (p) => { const d = phoneDigitsOf(p); return d.length >= 6 && d.length <= 20; };

// Which picks were already chosen (the same file picked twice must not be sent twice).
const fileSignature = (f) => `${f.name}|${f.size}|${f.lastModified}`;

// A PDF has no picture to show — a document card with its name and size stands in for it (shared with the shop's queue).
const pdfFace = (file, size) => (
    <div className="absolute inset-0">
        <BoothPdfFace name={file.name} detail={`PDF · ${formatBoothSize(file.size)}`} size={size} />
    </div>
);

export default function PhotoCollectUpload() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    // Counts in a message follow the page's language (Bangla digits unless the page is English).
    const num = (n) => (i18n.language?.startsWith('en') ? String(n) : toBnDigits(n));

    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(null);
    const [notFound, setNotFound] = useState(false);
    // The shop's own choice: PDFs are accepted only when it switched "receive files too" on. A photos-only shop gets the
    // plain photo wording (and PDFs are turned away here as well as on the server); otherwise the "…Files" variants read.
    const filesOn = view?.acceptFiles === true;
    const tx = (key, opts) => t(`photoCollect.${key}${filesOn ? 'Files' : ''}`, opts);

    // Prefill the phone from a previous visit (stored on the first successful send).
    const [phone, setPhone] = useState(() => { try { return localStorage.getItem(PHONE_KEY) || ''; } catch { return ''; } });
    // "Delete my files after printing" — the customer's own choice, remembered like the phone.
    const [isPrivate, setIsPrivate] = useState(() => { try { return localStorage.getItem(PRIVATE_KEY) === '1'; } catch { return false; } });
    const changePrivate = (on) => {
        setIsPrivate(on);
        try { localStorage.setItem(PRIVATE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
    };
    // Photos and PDFs chosen but not sent yet: [{ id, file, kind: 'image' | 'pdf', url, error }]
    // (url = object URL for a photo's preview, null for a PDF; error = why the last send of it failed).
    const [items, setItems] = useState([]);
    const itemsRef = useRef([]);
    itemsRef.current = items;
    const [progress, setProgress] = useState(null); // { current, total } while sending
    const [sending, setSending] = useState(false);
    const [sentCount, setSentCount] = useState(0);
    // Booth filled up while this page was open (the send came back 409).
    const [boothFull, setBoothFull] = useState(false);

    const cameraRef = useRef(null);
    const galleryRef = useRef(null);

    // "Install as an app" (PWA): injects a per-shop manifest + Apple meta so this page
    // can be added to the home screen and reopens THIS shop in one tap.
    const install = useBoothInstall({ slug, enabled: !!view && !notFound });
    const [showInstallHelp, setShowInstallHelp] = useState(false);
    const handleInstallClick = async () => {
        // Android: fire the native install dialog when available; otherwise (and always
        // on iOS) show the manual "Add to Home Screen" steps.
        if (install.hasNativePrompt) { const ok = await install.promptInstall(); if (ok) return; }
        setShowInstallHelp(true);
    };

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await photoCollectorService.publicView(slug);
                if (alive) setView(res?.data || null);
            } catch { if (alive) setNotFound(true); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [slug]);

    // Free any remaining object URLs when the page goes away.
    useEffect(() => () => { itemsRef.current.forEach((it) => it.url && URL.revokeObjectURL(it.url)); }, []);

    // The server's reason for refusing a file, in the customer's language (falls back to the server's own words).
    const reasonOf = (err) => (err?.code && i18n.exists(`photoCollect.err.${err.code}`)
        ? t(`photoCollect.err.${err.code}`)
        : (err?.message || t('photoCollect.failedMsg')));

    // No crop here — the photos are sent as-is; the shop owner crops them in the
    // editor when loading them from the Photo Booth. There is no limit on how many can be sent.
    // `list` is already an Array (callers snapshot the FileList BEFORE resetting the input value,
    // which empties a live FileList).
    const pickFiles = async (list) => {
        if (!list.length) return;
        // The type is checked from the file's own bytes (not its name), the size per kind.
        const checks = await Promise.all(list.map((f) => checkBoothFile(f, { allowPdf: filesOn })));
        const have = new Set(itemsRef.current.map((it) => fileSignature(it.file)));
        const next = [];
        let badType = false, tooBig = false;
        list.forEach((f, i) => {
            const check = checks[i];
            if (!check.ok) { if (check.reason === 'tooLarge') tooBig = true; else badType = true; return; }
            const sig = fileSignature(f);
            if (have.has(sig)) return; // already in the list
            have.add(sig);
            next.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file: f, kind: check.kind, url: check.kind === 'image' ? URL.createObjectURL(f) : null, error: '' });
        });
        if (badType) notifications.show({ title: t('photoCollect.invalidFileTitle'), message: tx('invalidFileMsg'), color: 'red' });
        if (tooBig) notifications.show({ title: t('photoCollect.tooLargeTitle'), message: tx('tooLargeMsg'), color: 'red' });
        if (next.length) setItems((prev) => [...prev, ...next]);
    };

    const removeItem = (id) => {
        setItems((prev) => {
            const gone = prev.find((it) => it.id === id);
            if (gone?.url) URL.revokeObjectURL(gone.url);
            return prev.filter((it) => it.id !== id);
        });
    };

    const clearFiles = () => {
        itemsRef.current.forEach((it) => it.url && URL.revokeObjectURL(it.url));
        setItems([]);
    };

    const phoneValid = phoneOk(phone);
    const hasItems = items.length > 0;
    const canSend = phoneValid && hasItems && !sending;

    const handleSend = async () => {
        if (!canSend) {
            if (!phoneValid) notifications.show({ title: t('photoCollect.needPhoneTitle'), message: t('photoCollect.needPhoneMsg'), color: 'orange' });
            return;
        }
        setSending(true);
        setItems((prev) => prev.map((it) => ({ ...it, error: '' })));
        // One request per file (the endpoint takes a single file). Sent in order: a booth that fills up part-way
        // stops cleanly and the unsent files stay in the list; a file the server refuses is marked and the rest go on.
        const batch = itemsRef.current.slice();
        let sent = 0;
        let failed = 0;
        let firstFailure = '';
        let stopped = false;
        try {
            for (let n = 0; n < batch.length; n++) {
                setProgress({ current: n + 1, total: batch.length });
                try {
                    const fd = new FormData();
                    fd.append('file', batch[n].file);
                    fd.append('phone', phone.trim());
                    fd.append('private', isPrivate ? '1' : '0');
                    await photoCollectorService.upload(slug, fd);
                    sent++;
                    removeItem(batch[n].id);
                } catch (err) {
                    // Booth is full (409) — there's no per-number limit anymore.
                    if (err?.status === 409) { setBoothFull(true); stopped = true; break; }
                    const reason = reasonOf(err);
                    // Too many uploads right now (429): nothing is wrong with the files — they stay in the list to send again in a moment.
                    if (err?.status === 429) { failed++; firstFailure = firstFailure || reason; stopped = true; break; }
                    // Nothing about the file itself (offline, booth closed, phone refused): the rest would only fail the same way.
                    if (!err?.status || err.status === 403 || err.status === 404 || err.code === 'BOOTH_INVALID_PHONE') {
                        failed++;
                        firstFailure = firstFailure || reason;
                        stopped = true;
                        break;
                    }
                    failed++;
                    firstFailure = firstFailure || reason;
                    setItems((prev) => prev.map((it) => (it.id === batch[n].id ? { ...it, error: reason } : it)));
                }
            }
            if (sent > 0 && failed === 0 && !stopped) {
                notifications.show({
                    title: t('photoCollect.sentTitle'),
                    message: sent > 1 ? tx('sentManyMsg', { count: num(sent) }) : tx('sentMsg'),
                    color: 'green',
                });
            } else if (sent > 0) {
                notifications.show({ title: tx('partialTitle'), message: tx('partialMsg', { sent: num(sent), total: num(batch.length) }), color: 'orange' });
            } else if (failed > 0) {
                notifications.show({ title: t('photoCollect.failedTitle'), message: firstFailure, color: 'red' });
            }
        } finally {
            if (sent > 0) {
                // Remember the number for next time now that we know it worked.
                try { localStorage.setItem(PHONE_KEY, phone.trim()); } catch { /* ignore */ }
                setSentCount((c) => c + sent);
            }
            setProgress(null);
            setSending(false);
        }
    };

    // ── Full-screen state screens (match the one-time QR page) ────────────────
    const screen = (children) => (
        <div className="min-h-[100dvh] bg-white dark:bg-zinc-900 p-6 flex flex-col">
            <div className="mb-6">
                <a href="https://sohozkaj.com/" target="_blank" rel="noopener noreferrer"><img src="/images/logo.svg" alt="SohozKaj" className="w-28" /></a>
            </div>
            <div className="flex items-center justify-center flex-1">
                <div className="flex flex-col items-center gap-4 max-w-[360px] w-full text-center">{children}</div>
            </div>
        </div>
    );

    if (loading) {
        return screen(<><div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" /><p className="text-sm text-zinc-500 dark:text-zinc-400">{t('photoCollect.loading')}</p></>);
    }
    if (notFound || !view) {
        return screen(<><IconAlertTriangle size={44} className="text-zinc-400" /><h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t('photoCollect.unavailableTitle')}</h2><p className="text-sm text-zinc-500 dark:text-zinc-400">{t('photoCollect.unavailableMsg')}</p></>);
    }
    if (!view.enabled || view.closedNow) {
        // Booth switched off (or outside business hours) — lead with the shop's
        // identity, then the closed note.
        return screen(<>
            {view.shopLogo
                ? <img src={view.shopLogo} alt="" className="w-16 h-16 rounded-full object-cover border border-zinc-200 dark:border-zinc-700" />
                : <div className="text-[52px]">🚫</div>}
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{view.shopName || t('photoCollect.defaultShop')}</h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('photoCollect.closedMsg')}</p>
        </>);
    }
    if (boothFull || (view.full && sentCount === 0)) {
        return screen(<><div className="text-[52px]">📥</div><h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t('photoCollect.fullTitle')}</h2><p className="text-sm text-zinc-500 dark:text-zinc-400">{t('photoCollect.fullMsg')}</p></>);
    }

    const removeButton = (it, small) => (
        <button onClick={() => removeItem(it.id)} disabled={sending} aria-label={t('photoCollect.remove')} className={`absolute rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-50 ${small ? 'top-1.5 right-1.5 p-1' : 'top-2 right-2 p-1.5'}`}>
            <IconX size={small ? 16 : 18} />
        </button>
    );
    // What went wrong with one file, written over its tile.
    const errorOverlay = (it) => it.error && (
        <span className="absolute inset-x-0 bottom-0 bg-red-600/90 px-2 py-1.5 text-center text-[10px] font-semibold leading-tight text-white">{it.error}</span>
    );

    // ── Main upload screen ────────────────────────────────────────────────────
    return (
        <div className="min-h-[100dvh] bg-white dark:bg-zinc-900 flex flex-col p-4 pb-8">
            {/* Top bar: SohozKaj (links home) left · shop identity right, one line */}
            <div className="mb-2 flex items-center justify-between gap-3">
                <a href="https://sohozkaj.com/" target="_blank" rel="noopener noreferrer" className="flex-none">
                    <img src="/images/logo.svg" alt="SohozKaj" className="w-24" />
                </a>
                <div className="flex items-center gap-2 min-w-0">
                    {view.shopLogo
                        ? <img src={view.shopLogo} alt="" className="w-8 h-8 rounded-full object-cover border border-zinc-200 dark:border-zinc-700 flex-none" />
                        : <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none"><IconPhoto size={16} /></span>}
                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate">{view.shopName || t('photoCollect.defaultShop')}</p>
                </div>
            </div>
            {/* Subtitle + the install ("Add to home screen") button on its right */}
            <div className="flex items-center justify-between gap-2 mb-4">
                <p className="text-xs text-zinc-500 dark:text-zinc-400 min-w-0">{tx('subtitle')}</p>
                {install.canShow && (
                    <button onClick={handleInstallClick} className="flex-none inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-white text-xs font-semibold">
                        <IconDeviceMobilePlus size={15} /> {t('photoCollect.installBtn')}
                    </button>
                )}
            </div>

            {/* Phone only — no name is collected */}
            <div className="mb-3">
                <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9+]/g, '').slice(0, 20))} type="tel" inputMode="tel" autoComplete="tel" placeholder={t('photoCollect.phonePlaceholder')}
                    className="w-full px-3.5 py-2.5 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                {phoneDigitsOf(phone).length > 0 && !phoneValid && <p className="text-[11px] text-red-500 mt-1">{t('photoCollect.needPhoneMsg')}</p>}
            </div>

            {/* Privacy: the customer chooses whether what they send is deleted once the shop has printed / used it */}
            <label htmlFor="booth-private" className="mb-4 flex cursor-pointer items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary/10 text-primary"><IconLock size={16} /></span>
                <span className="min-w-0 flex-1 text-sm font-semibold leading-tight text-zinc-800 dark:text-zinc-100">{tx('privateTitle')}</span>
                <Switch id="booth-private" checked={isPrivate} onCheckedChange={changePrivate} disabled={sending} aria-label={tx('privateTitle')} />
            </label>

            {/* Photos / PDFs — placeholder covers the whole available area (tap to pick) */}
            {!hasItems && (
                <button type="button" onClick={() => galleryRef.current?.click()}
                    className="flex-1 relative w-full rounded-2xl overflow-hidden mb-3 block p-0 bg-zinc-100 dark:bg-zinc-800" style={{ minHeight: 240 }}>
                    <img src="/images/face-placeholder.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
                    {sentCount > 0 && (
                        <span className="absolute top-3 left-1/2 -translate-x-1/2 z-[1] inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold shadow-md">
                            <IconCheck size={14} stroke={3} /> {t('photoCollect.sentBadge')}
                        </span>
                    )}
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent px-4 pt-8 pb-3 text-center text-xs font-medium text-white">{tx('pickHint')}</span>
                </button>
            )}
            {items.length === 1 && (
                items[0].kind === 'image' ? (
                    <div className="flex-1 relative rounded-xl overflow-hidden bg-black flex items-center justify-center mb-3" style={{ maxHeight: '52dvh' }}>
                        <img src={items[0].url} alt="preview" style={{ maxWidth: '100%', maxHeight: '52dvh', objectFit: 'contain', display: 'block' }} />
                        {sending && <span className="absolute inset-0 bg-black/45 flex items-center justify-center"><span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></span>}
                        {errorOverlay(items[0])}
                        {removeButton(items[0])}
                    </div>
                ) : (
                    <div className="flex-1 relative rounded-xl overflow-hidden mb-3" style={{ minHeight: 220, maxHeight: '52dvh' }}>
                        {pdfFace(items[0].file, 'lg')}
                        {sending && <span className="absolute inset-0 bg-black/30 flex items-center justify-center"><span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></span>}
                        {errorOverlay(items[0])}
                        {removeButton(items[0])}
                    </div>
                )
            )}
            {items.length > 1 && (
                <div className="flex-1 min-h-0 overflow-y-auto mb-3" style={{ maxHeight: '52dvh' }}>
                    <div className="grid grid-cols-2 min-[430px]:grid-cols-3 gap-2 content-start">
                        {items.map((it, idx) => (
                            <div key={it.id} className="relative rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800" style={{ aspectRatio: '3 / 4' }}>
                                {it.kind === 'image'
                                    ? <img src={it.url} alt={`preview ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                                    : pdfFace(it.file, 'sm')}
                                {sending && progress?.current === idx + 1 && (
                                    <span className="absolute inset-0 bg-black/45 flex items-center justify-center"><span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></span>
                                )}
                                {errorOverlay(it)}
                                {removeButton(it, true)}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3 mt-auto pt-4">
                {!hasItems ? (
                    <>
                        <button className="px-4 py-3 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 w-full text-white bg-orange-500 hover:bg-orange-600 transition-colors" onClick={() => cameraRef.current?.click()}>
                            <IconCamera size={20} /> {t('photoCollect.takePhoto')}
                        </button>
                        <button className="px-4 py-3 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 w-full bg-transparent border border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950 transition-colors" onClick={() => galleryRef.current?.click()}>
                            <IconPhoto size={20} /> {t(filesOn ? 'photoCollect.chooseFiles' : 'photoCollect.choosePhotos')}
                        </button>
                    </>
                ) : (
                    <>
                        <button className="px-4 py-3.5 rounded-xl font-bold text-base inline-flex items-center justify-center gap-2 w-full text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50" disabled={!canSend} onClick={handleSend}>
                            {sending ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <IconSend size={22} />}
                            {sending && progress
                                ? t('photoCollect.sendingProgress', { current: num(progress.current), total: num(progress.total) })
                                : items.length > 1 ? tx('sendManyBtn', { count: num(items.length) }) : tx('sendBtn')}
                        </button>
                        <button disabled={sending} className="px-4 py-2.5 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 w-full bg-transparent border border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950 transition-colors disabled:opacity-50" onClick={() => galleryRef.current?.click()}>
                            <IconPlus size={18} /> {t('photoCollect.addMore')}
                        </button>
                        <button disabled={sending} className="px-4 py-2.5 rounded-xl text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 font-medium text-sm inline-flex items-center justify-center gap-2 w-full disabled:opacity-50" onClick={clearFiles}>
                            {tx('chooseDifferent')}
                        </button>
                    </>
                )}
            </div>

            <p className="text-[11px] text-center text-zinc-400 dark:text-zinc-500 mt-3">{tx('footerNote')}</p>

            {/* FileList is live: snapshot it with Array.from BEFORE resetting the value, or it empties. */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ''; pickFiles(list); }} />
            <input ref={galleryRef} type="file" accept={filesOn ? BOOTH_ACCEPT : BOOTH_PHOTO_ACCEPT} multiple style={{ display: 'none' }} onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ''; pickFiles(list); }} />

            {/* Manual "Add to Home Screen" steps — iOS always, and Android when the
                browser doesn't offer a native install dialog. */}
            {showInstallHelp && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3" onClick={() => setShowInstallHelp(false)}>
                    <div className="w-full max-w-[360px] bg-white dark:bg-zinc-900 rounded-2xl p-5 pb-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <img src="/pwa/booth-icon-192.png" alt="" className="w-11 h-11 rounded-xl flex-none" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 leading-tight">{t('photoCollect.installHelpTitle')}</p>
                                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{view.shopName || t('photoCollect.defaultShop')}</p>
                            </div>
                            <button onClick={() => setShowInstallHelp(false)} aria-label={t('photoCollect.installDismiss')} className="flex-none p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"><IconX size={18} /></button>
                        </div>
                        <ol className="space-y-2.5">
                            <li className="flex items-center gap-2.5 text-[13px] text-zinc-700 dark:text-zinc-200">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-none">1</span>
                                <span className="flex items-center gap-1.5">{t(install.platform === 'ios' ? 'photoCollect.installIosStep1' : 'photoCollect.installAndroidStep1')}{install.platform === 'ios' ? <IconShare size={16} className="text-primary" /> : <IconDotsVertical size={16} className="text-primary" />}</span>
                            </li>
                            <li className="flex items-center gap-2.5 text-[13px] text-zinc-700 dark:text-zinc-200">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-none">2</span>
                                <span>{t(install.platform === 'ios' ? 'photoCollect.installIosStep2' : 'photoCollect.installAndroidStep2')}</span>
                            </li>
                        </ol>
                        <button onClick={() => setShowInstallHelp(false)} className="mt-5 w-full px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold">{t('photoCollect.installDone')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
